import { createContext, useContext, useEffect, useState } from "react";
import { logApp } from "@/utils/logApp";
import config, { onConfigChange, type ResolvedConfig } from "../../config";
import { updateApiConfig } from "../../services/api.service";

interface ApiContextType {
	isInitialized: boolean;
	error: Error | null;
	config: ResolvedConfig | null;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export function useApi() {
	const context = useContext(ApiContext);
	if (context === undefined) {
		throw new Error("useApi must be used within an ApiProvider");
	}
	return context;
}

interface ApiProviderProps {
	children: React.ReactNode;
}

export function ApiProvider({ children }: ApiProviderProps) {
	const [isInitialized, setIsInitialized] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [currentConfig, setCurrentConfig] = useState<ResolvedConfig | null>(
		null,
	);

	useEffect(() => {
		let isMounted = true;

		async function initializeApi() {
			try {
				// Wait for configuration to be loaded
				await config.waitFor();
				const configData = config.get();

				if (isMounted) {
					setCurrentConfig(configData);
				}

				// Initialize API service with loaded configuration
				// console.log("🔧 API Provider: Initializing API service with config");
				// apiService reads config automatically, no need to pass parameters
				updateApiConfig();

				if (isMounted) {
					setIsInitialized(true);
				}
			} catch (err) {
				if (isMounted) {
					setError(
						err instanceof Error ? err : new Error("Failed to initialize API"),
					);
					logApp({
						level: "error",
						message: `API Provider: Failed to initialize API: ${err}`,
					});
				}
			}
		}

		initializeApi();

		return () => {
			isMounted = false;
		};
	}, []);

	// Listen for configuration changes
	useEffect(() => {
		const unsubscribe = onConfigChange((newConfig) => {
			console.log("🔄 API Provider: Configuration changed, updating...");
			setCurrentConfig(newConfig);

			// Update API service with new configuration
			// apiService reads config automatically, no need to pass parameters
			updateApiConfig();
			console.log("✅ API Service: Updated with new configuration");
		});

		return unsubscribe;
	}, []);

	const value = {
		isInitialized,
		error,
		config: currentConfig,
	};

	return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

/**
 * Loading component to show while API is initializing
 */
export function ApiInitializer({ children }: { children: React.ReactNode }) {
	const { error } = useApi();

	if (error) {
		return (
			<div>
				Application configuration failed to load. Please reload the page.
				{error.message}
			</div>
		);
	}

	return <>{children}</>;
}
